import React from "react";
import Seo from "@/components/Seo";
import { LegalLayout, Clause, Bullets } from "@/components/pages/TermsPage";
import { CONTACT_EMAIL } from "@/config/site";

const COLLECTED = [
  "Identity and contact details: your name, work email, phone or WhatsApp number, company name and job title.",
  "Service information: the sales tools, CRM, markets, company size and target customers you tell us about, so we can prepare your audit or build.",
  "Correspondence: the content of your enquiries, audit requests and emails with us.",
  "Billing details: the company name, billing address and invoice records needed to raise and settle invoices.",
  "Technical data: IP address and basic request information, which we use to rate limit our forms and keep them free of spam.",
];

const PURPOSES = [
  "To deliver the service you asked for and to prepare for your audit or discovery call.",
  "To reply to your enquiry and to contact you about work in progress.",
  "To raise invoices, take payment and keep accounting records.",
  "To protect our forms from automated abuse.",
  "To send you updates you have asked for. Every marketing email has an unsubscribe link.",
];

const PROCESSORS = [
  "Resend, to deliver transactional and notification email.",
  "MongoDB, as our database host.",
  "Google Analytics and PostHog, to understand how the website is used.",
  "Our website and application hosting providers.",
];

const RIGHTS = [
  "Ask for access to the personal data we hold about you and how we have used it in the past year.",
  "Ask us to correct personal data that is inaccurate or incomplete.",
  "Withdraw your consent to us using your data for a given purpose, at any time.",
  "Ask us to delete personal data we no longer have a legal or business reason to keep.",
];

export default function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" updated="June 2026" testId="privacy-page">
      <Seo
        title="Privacy Policy"
        description="How Raki Solutions collects, uses, discloses and protects personal data, in line with Singapore's Personal Data Protection Act."
        path="/privacy"
      />

      <Clause heading="1. About this policy" testId="privacy-about">
        <p>
          Raki Solutions is based in Singapore and handles personal data in line with the Personal
          Data Protection Act 2012 (PDPA). This policy explains what we collect, why, who we share
          it with, how long we keep it and the rights you have over it.
        </p>
        <p>
  "Personal data" means data, true or not, about an individual who can be identified from
          that data, or from that data combined with other information we have or are likely to have
          access to.
        </p>
      </Clause>

      <Clause heading="2. What we collect" testId="privacy-collect">
        <p>When you contact us, ask for an audit or buy a service, we may collect:</p>
        <Bullets items={COLLECTED} />
      </Clause>

      <Clause heading="3. Consent and why we use your data" testId="privacy-purpose">
        <p>
          By submitting a form or accepting a proposal, you consent to us collecting, using and
          disclosing your personal data for the purposes below. Where the PDPA allows us to act
          without consent, for example to perform a contract you have entered into with us or to
          meet a legal obligation, we rely on that instead.
        </p>
        <Bullets items={PURPOSES} />
        <p>
          We do not sell your personal data, and we do not use it to train any machine learning
          model.
        </p>
      </Clause>

      <Clause heading="4. Who we share it with" testId="privacy-disclosure">
        <p>
          We share personal data only with the service providers we need to run the business, and
          only to the extent they need it:
        </p>
        <Bullets items={PROCESSORS} />
        <p>
          Some of these providers process data outside Singapore. Where that happens, we take
          reasonable steps to ensure a standard of protection comparable to the PDPA, as required by
          the Transfer Limitation Obligation. We may also disclose data where the law requires it.
        </p>
      </Clause>

      <Clause heading="5. How long we keep it" testId="privacy-retention">
        <p>
          We keep personal data only for as long as it serves the purpose it was collected for, or as
          long as the law requires. Enquiry and intake records are kept while a commercial
          relationship is reasonably in prospect and for up to 24 months after our last contact.
          Payment and accounting records are kept for 5 years, as Singapore tax law requires. After
          that, data is deleted or anonymised.
        </p>
      </Clause>

      <Clause heading="6. How we protect it" testId="privacy-protection">
        <p>
          Data is transmitted over encrypted connections and stored in access-controlled systems.
          Access is limited to the people who need it to do the work. No system is perfectly secure, but we take reasonable technical and organisational measures
          to prevent unauthorised access, collection, use, disclosure, copying, modification or
          disposal.
        </p>
      </Clause>

      <Clause heading="7. Your rights under the PDPA" testId="privacy-rights">
        <p>You have the right to:</p>
        <Bullets items={RIGHTS} />
        <p>
          Email{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#C8703A] font-semibold hover:underline underline-offset-4">{CONTACT_EMAIL}</a>{" "}
          to make a request and we will respond within 30 days. If withdrawing consent means we can
          no longer deliver a service you have asked for, we will tell you what that means before we
          act on it. A reasonable fee may apply to access requests that take significant work.
        </p>
      </Clause>

      <Clause heading="8. Cookies and analytics" testId="privacy-cookies">
        <p>
          We use Google Analytics and PostHog to understand how people use the website, for example
          which pages are visited and where visitors arrive from. These set cookies in your browser.
          We do not run advertising cookies and we do not sell data to advertisers. You can block
          cookies in your browser settings without losing access to the site.
        </p>
      </Clause>

      <Clause heading="9. Data Protection Officer and complaints" testId="privacy-dpo">
        <p>
          Our Data Protection Officer can be reached at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#C8703A] font-semibold hover:underline underline-offset-4">{CONTACT_EMAIL}</a>.
          Please raise any concern with us first. If you are not satisfied with our response, you can
          escalate to the Personal Data Protection Commission of Singapore at pdpc.gov.sg.
        </p>
      </Clause>

      <Clause heading="10. Changes to this policy" testId="privacy-changes">
        <p>
          We may update this policy as our services or the law change. The version published here is
          the one in force, and the date at the top tells you when it last changed.
        </p>
      </Clause>
    </LegalLayout>
  );
}
